import React, { useState, useContext } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { motion } from "framer-motion";
import { FaUserCircle } from "react-icons/fa";
import { Form, Button, Spinner, Modal } from "react-bootstrap";
import { GoogleLogin } from "@react-oauth/google";

export default function Login() {
  const { login } = useContext(AuthContext);
  const [isSignUp, setIsSignUp] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [signupState, setSignupState] = useState("");
  const [signupCity, setSignupCity] = useState("");
  const [addressError, setAddressError] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetFeedback, setResetFeedback] = useState({
    message: "",
    variant: "",
  });
  const [resetLoading, setResetLoading] = useState(false);
  const [signupOtp, setSignupOtp] = useState("");
  const [signupOtpStatus, setSignupOtpStatus] = useState({
    message: "",
    variant: "",
  });
  const [signupOtpSending, setSignupOtpSending] = useState(false);
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [resetOtp, setResetOtp] = useState("");
  const [resetOtpStatus, setResetOtpStatus] = useState({
    message: "",
    variant: "",
  });
  const [resetOtpSending, setResetOtpSending] = useState(false);
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const nav = useNavigate();

  const handleSignupOtpRequest = async () => {
    setSignupOtpStatus({ message: "", variant: "" });
    setError("");
    if (!phone) {
      setSignupOtpStatus({
        message: "Enter your phone number before requesting an OTP.",
        variant: "danger",
      });
      return;
    }

    setSignupOtpSending(true);
    try {
      await api.post("/auth/phone/request-otp", {
        phone,
        email,
        purpose: "signup",
      });
      setSignupOtpSent(true);
      setSignupOtpStatus({
        message: "OTP sent to your phone number.",
        variant: "success",
      });
    } catch (err) {
      setSignupOtpStatus({
        message: err.response?.data?.message || err.message,
        variant: "danger",
      });
    } finally {
      setSignupOtpSending(false);
    }
  };

  const handleResetOtpRequest = async () => {
    setResetOtpStatus({ message: "", variant: "" });
    if (!resetEmail || !resetPhone) {
      setResetOtpStatus({
        message:
          "Enter your registered email and phone before requesting an OTP.",
        variant: "danger",
      });
      return;
    }

    setResetOtpSending(true);
    try {
      await api.post("/auth/phone/request-otp", {
        phone: resetPhone,
        email: resetEmail,
        purpose: "reset",
      });
      setResetOtpSent(true);
      setResetOtpStatus({
        message: "OTP sent to your phone number.",
        variant: "success",
      });
    } catch (err) {
      setResetOtpStatus({
        message: err.response?.data?.message || err.message,
        variant: "danger",
      });
    } finally {
      setResetOtpSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setAddressError("");
    setLoading(true);
    try {
      let res;
      if (isSignUp) {
        if (!signupState || !signupCity) {
          setAddressError(
            "Please choose your address from the suggestions so we can capture city and state."
          );
          setLoading(false);
          return;
        }
        if (!signupOtpSent) {
          setError("Please request an OTP before completing signup.");
          setLoading(false);
          return;
        }
        if (!signupOtp) {
          setError("Please enter the OTP sent to your phone.");
          setLoading(false);
          return;
        }
        res = await api.post("/auth/signup", {
          username,
          email,
          phone,
          password,
          state: signupState,
          city: signupCity,
          verificationCode: signupOtp,
        });
        setSignupOtp("");
        setSignupOtpSent(false);
        setSignupOtpStatus({ message: "", variant: "" });
      } else {
        res = await api.post("/auth/login", { phone, password });
      }
      // Save user in context and redirect
      login(res.data.user);
      nav("/market");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetFeedback({ message: "", variant: "" });

    if (!resetEmail || !resetPhone || !resetPassword) {
      setResetFeedback({
        message: "Please fill in email, phone, and new password.",
        variant: "danger",
      });
      return;
    }

    if (resetPassword !== resetConfirm) {
      setResetFeedback({
        message: "New password and confirmation do not match.",
        variant: "danger",
      });
      return;
    }

    if (!resetOtpSent) {
      setResetFeedback({
        message: "Please request an OTP before updating your password.",
        variant: "danger",
      });
      return;
    }

    if (!resetOtp) {
      setResetFeedback({
        message: "Please enter the OTP sent to your phone.",
        variant: "danger",
      });
      return;
    }

    try {
      setResetLoading(true);
      await api.post("/auth/forgot-password", {
        email: resetEmail,
        phone: resetPhone,
        password: resetPassword,
        verificationCode: resetOtp,
      });
      setResetFeedback({
        message: "Password updated! You can now log in with the new password.",
        variant: "success",
      });
      setResetPassword("");
      setResetConfirm("");
      setResetOtp("");
      setResetOtpSent(false);
      setResetOtpStatus({ message: "", variant: "" });
    } catch (err) {
      setResetFeedback({
        message: err.response?.data?.message || err.message,
        variant: "danger",
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      setError("Google sign-in did not return a credential. Please try again.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await api.post("/auth/google", {
        credential: credentialResponse.credential,
      });
      login(res.data.user);
      nav("/market");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Google sign-in failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed. Please try again.");
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="card p-5 shadow-lg border-0 rounded-4"
        style={{ maxWidth: 450, width: "100%" }}
      >
        <div className="text-center mb-4">
          <div className="mb-3">
            <FaUserCircle size={64} className="text-primary" />
          </div>
          <h2 className="fw-bold text-gradient mb-2">
            {isSignUp ? "Create Account" : "Welcome Back"}
          </h2>
          <p className="text-muted mb-0">
            {isSignUp ? "Sign up to get started" : "Please login to continue"}
          </p>
        </div>
        <Form onSubmit={submit}>
          {isSignUp && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Username</Form.Label>
                <Form.Control
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  className="rounded-pill"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="rounded-pill"
                />
              </Form.Group>
              <AddressAutocomplete
                label="Address"
                required
                onPlaceSelected={(place) => {
                  const components = place?.address_components || [];
                  let city = "";
                  let state = "";
                  components.forEach((c) => {
                    if (c.types.includes("locality")) city = c.long_name;
                    if (
                      c.types.includes("administrative_area_level_2") &&
                      !city
                    )
                      city = c.long_name;
                    if (c.types.includes("administrative_area_level_1"))
                      state = c.long_name;
                  });
                  setSignupCity(city);
                  setSignupState(state);
                  if (!city || !state) {
                    setAddressError(
                      "Please select a precise address so we can detect your city and state."
                    );
                  } else {
                    setAddressError("");
                  }
                }}
              />
              {signupState && signupCity && (
                <div className="mb-3">
                  <strong>Selected:</strong> {signupState}, {signupCity}
                </div>
              )}
              {addressError && (
                <div className="alert alert-warning py-2">{addressError}</div>
              )}
            </>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Phone</Form.Label>
            {isSignUp ? (
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  required
                  className="rounded-pill"
                />
                <Button
                  type="button"
                  variant="outline-primary"
                  className="rounded-pill fw-semibold px-3"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={handleSignupOtpRequest}
                  disabled={signupOtpSending || !phone}
                >
                  {signupOtpSending ? (
                    <Spinner animation="border" size="sm" />
                  ) : signupOtpSent ? (
                    "Resend OTP"
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </div>
            ) : (
              <Form.Control
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                required
                className="rounded-pill"
              />
            )}
          </Form.Group>
          {isSignUp && signupOtpStatus.message && (
            <div
              className={`alert alert-${signupOtpStatus.variant} text-center rounded-pill py-2`}
            >
              {signupOtpStatus.message}
            </div>
          )}
          {isSignUp && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">OTP</Form.Label>
              <Form.Control
                type="text"
                value={signupOtp}
                onChange={(e) => setSignupOtp(e.target.value)}
                placeholder="Enter the OTP sent to your phone"
                required
                className="rounded-pill"
              />
            </Form.Group>
          )}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">Password</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              className="rounded-pill"
            />
          </Form.Group>
          {!isSignUp && (
            <div className="text-end mb-3">
              <button
                type="button"
                className="btn btn-link p-0 fw-semibold text-primary"
                onClick={() => {
                  setShowReset(true);
                  setResetFeedback({ message: "", variant: "" });
                  setResetOtp("");
                  setResetOtpSent(false);
                  setResetOtpStatus({ message: "", variant: "" });
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          {error && (
            <div className="alert alert-danger text-center rounded-pill py-2">
              {error}
            </div>
          )}
          <Button
            type="submit"
            variant="primary"
            className="w-100 rounded-pill fw-bold py-2 cta-btn"
            disabled={loading}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : isSignUp ? (
              "Sign Up"
            ) : (
              "Login"
            )}
          </Button>
          {!isSignUp && (
            <div className="text-center my-3">
              <span className="text-muted">or continue with</span>
            </div>
          )}
          {!isSignUp && (
            <div className="d-flex justify-content-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
              />
            </div>
          )}
        </Form>
        <div className="text-center mt-4">
          {isSignUp ? (
            <span className="text-muted">
              Already have an account?{" "}
              <button
                type="button"
                className="btn btn-link p-0 fw-semibold text-primary"
                onClick={() => {
                  setIsSignUp(false);
                  setSignupOtp("");
                  setSignupOtpSent(false);
                  setSignupOtpStatus({ message: "", variant: "" });
                  setError("");
                }}
              >
                Login
              </button>
            </span>
          ) : (
            <span className="text-muted">
              New user?{" "}
              <button
                type="button"
                className="btn btn-link p-0 fw-semibold text-primary"
                onClick={() => {
                  setIsSignUp(true);
                  setError("");
                  setAddressError("");
                  setSignupOtp("");
                  setSignupOtpSent(false);
                  setSignupOtpStatus({ message: "", variant: "" });
                }}
              >
                Sign Up
              </button>
            </span>
          )}
        </div>
      </motion.div>

      <Modal
        show={showReset}
        onHide={() => {
          setShowReset(false);
          setResetEmail("");
          setResetPhone("");
          setResetPassword("");
          setResetConfirm("");
          setResetFeedback({ message: "", variant: "" });
          setResetOtp("");
          setResetOtpSent(false);
          setResetOtpStatus({ message: "", variant: "" });
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePasswordReset}>
          <Modal.Body>
            {resetFeedback.message && (
              <div className={`alert alert-${resetFeedback.variant}`}>
                {resetFeedback.message}
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your registered email"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  value={resetPhone}
                  onChange={(e) => setResetPhone(e.target.value)}
                  placeholder="Enter your registered phone"
                  required
                />
                <Button
                  type="button"
                  variant="outline-primary"
                  className="fw-semibold px-3"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={handleResetOtpRequest}
                  disabled={
                    resetOtpSending ||
                    !resetPhone ||
                    !resetEmail ||
                    resetLoading
                  }
                >
                  {resetOtpSending ? (
                    <Spinner animation="border" size="sm" />
                  ) : resetOtpSent ? (
                    "Resend OTP"
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </div>
            </Form.Group>
            {resetOtpStatus.message && (
              <div
                className={`alert alert-${resetOtpStatus.variant} py-2 mb-3`}
              >
                {resetOtpStatus.message}
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>OTP</Form.Label>
              <Form.Control
                type="text"
                value={resetOtp}
                onChange={(e) => setResetOtp(e.target.value)}
                placeholder="Enter the OTP sent to your phone"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowReset(false);
                setResetEmail("");
                setResetPhone("");
                setResetPassword("");
                setResetConfirm("");
                setResetFeedback({ message: "", variant: "" });
                setResetOtp("");
                setResetOtpSent(false);
                setResetOtpStatus({ message: "", variant: "" });
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={resetLoading}>
              {resetLoading ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Update Password"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
